import React, { useState, useEffect } from "react";
import { publicApi } from "../../../api/axiosConfig";
import FilterSideBarUI from "./FilterSideBarUI";

// Helper: Convert Persian/Arabic digits to English digits
const toEnglishDigits = (str) => {
  if (!str && str !== 0) return "";
  return String(str)
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));
};

export default function FilterSideBar({
  activeFilters,
  onApplyFilters,
  onClose,
  categorySlug,
  meta = {}, // Passed from parent ProductList view (contains category_brands & category_features)
}) {
  const brands = meta.category_brands || [];
  const categoryFeatures = meta.category_features || [];

  const [brandSearchQuery, setBrandSearchQuery] = useState("");
  const [localFilters, setLocalFilters] = useState(activeFilters || {});
  const [previewCount, setPreviewCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);

  // Sync internal state when external props change
  useEffect(() => {
    setLocalFilters(activeFilters || {});
  }, [activeFilters]);

  // Debounced API call to calculate live result count
  useEffect(() => {
    const controller = new AbortController();

    const cleanFilters = {};
    Object.entries(localFilters).forEach(([key, value]) => {
      if (
        value !== "" &&
        value !== null &&
        value !== undefined &&
        value !== false
      ) {
        cleanFilters[key] = value;
      }
    });

    const hasUserFiltered = Object.keys(cleanFilters).length > 0;

    if (!hasUserFiltered) {
      setPreviewCount(null);
      setLoadingCount(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoadingCount(true);

      if (categorySlug) {
        cleanFilters.category = categorySlug;
      }

      try {
        const res = await publicApi.get("/api/v1/products/", {
          params: {
            ...cleanFilters,
            page_size: 1,
          },
          signal: controller.signal,
        });

        if (typeof res.data?.count === "number") {
          setPreviewCount(res.data.count);
        } else {
          setPreviewCount(null);
        }
      } catch (err) {
        if (!publicApi.isCancel(err) && err.name !== "CanceledError") {
          console.error("Failed to fetch preview count", err);
          setPreviewCount(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingCount(false);
        }
      }
    }, 350);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [localFilters, categorySlug]);

  const handleDiscountChange = (e) => {
    setLocalFilters((prev) => ({
      ...prev,
      has_discount: e.target.checked ? true : "",
    }));
  };

  // Toggle Brand Slug in URL query (e.g. ?brand=apple,samsung)
  const handleBrandChange = (brandSlug) => {
    let currentBrands = localFilters.brand
      ? String(localFilters.brand).split(",").filter(Boolean)
      : [];

    if (currentBrands.includes(brandSlug)) {
      currentBrands = currentBrands.filter((b) => b !== brandSlug);
    } else {
      currentBrands.push(brandSlug);
    }

    setLocalFilters((prev) => ({
      ...prev,
      brand: currentBrands.length > 0 ? currentBrands.join(",") : "",
    }));
  };

  // Toggle Dynamic Feature Values in URL query (e.g. ?feature=RAM:8GB,Color:Black)
  const handleFeatureChange = (featureName, optionValue) => {
    const targetPair = `${featureName}:${optionValue}`;
    let currentFeatures = localFilters.feature
      ? String(localFilters.feature).split(",").filter(Boolean)
      : [];

    if (currentFeatures.includes(targetPair)) {
      currentFeatures = currentFeatures.filter((item) => item !== targetPair);
    } else {
      currentFeatures.push(targetPair);
    }

    setLocalFilters((prev) => ({
      ...prev,
      feature: currentFeatures.length > 0 ? currentFeatures.join(",") : "",
    }));
  };

  const isFeatureChecked = (featureName, optionValue) => {
    if (!localFilters.feature) return false;
    const targetPair = `${featureName}:${optionValue}`;
    return String(localFilters.feature).split(",").includes(targetPair);
  };

  const formatNumberWithCommas = (value) => {
    if (!value && value !== 0) return "";
    const cleanNum = toEnglishDigits(value).replace(/\D/g, "");
    if (!cleanNum) return "";
    return Number(cleanNum).toLocaleString("en-US");
  };

  const handlePriceChange = (type, e) => {
    const rawValue = toEnglishDigits(e.target.value).replace(/\D/g, "");
    setLocalFilters((prev) => ({
      ...prev,
      [type]: rawValue !== "" ? rawValue : "",
    }));
  };

  const handleApply = () => {
    onApplyFilters(localFilters);
    if (onClose) onClose();
  };

  const handleClear = () => {
    setLocalFilters({});
  };

  const filteredBrands = brands.filter((brand) =>
    brand.name.toLowerCase().includes(brandSearchQuery.toLowerCase())
  );

  const hasActiveFilters = Object.values(localFilters).some(
    (val) => val !== "" && val !== null && val !== undefined && val !== false
  );

  return (
    <FilterSideBarUI
      brands={brands}
      categoryFeatures={categoryFeatures}
      brandSearchQuery={brandSearchQuery}
      onBrandSearchChange={setBrandSearchQuery}
      localFilters={localFilters}
      filteredBrands={filteredBrands}
      hasActiveFilters={hasActiveFilters}
      previewCount={previewCount}
      loadingCount={loadingCount}
      onDiscountChange={handleDiscountChange}
      onBrandChange={handleBrandChange}
      onFeatureChange={handleFeatureChange}
      isFeatureChecked={isFeatureChecked}
      onPriceChange={handlePriceChange}
      onApply={handleApply}
      onClear={handleClear}
      formatNumberWithCommas={formatNumberWithCommas}
    />
  );
}
